import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { usePUR1040_R01Store } from "./Store_PUR1040_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultPUR1040_R01DetailDto, PUR1040_R01DetailDto } from "./Dto_PUR1040_R01";

import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR1040_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR1040_R01DetailDto> | null>;
}

export const DetailTable_PUR1040_R01 = (props: Props) => {

	const { gridRef } = props;

	const { 
			PUR1040_R01DetailList, setPUR1040_R01DetailList, 
			selectedPUR1040_R01DetailList, setSelectedPUR1040_R01DetailList, removeSelectedPUR1040_R01DetailList, addSelectedPUR1040_R01DetailList, initSelectedPUR1040_R01DetailList,
			deletedPUR1040_R01DetailList, setDeletedPUR1040_R01DetailList, addDeletedPUR1040_R01DetailList, removeDeletedPUR1040_R01DetailList,
			insertPUR1040_R01DetailList, updatePUR1040_R01DetailList, removePUR1040_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalPUR1040_R01DetailList, setOriginalPUR1040_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = usePUR1040_R01Store();
	const { unitList } = useCommonCodeStore();
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div에 대한 ref 생성

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PUR1040_R01DetailList,
		defaultGridData: defaultPUR1040_R01DetailDto,
		selectedRow: selectedPUR1040_R01DetailList,
		originalGridData: originalPUR1040_R01DetailList,
		updateRow: (index: number, data: any) => updatePUR1040_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		modelNo: '모델명',
		brandNm: '브랜드',
		itemGroupNm: '품목그룹',
		itemAccountNm: '품목계정',
		orderUnit: '단위',
		orderQty: '수량',
		orderPrice: '단가',
		dcAmt: '할인액',
		itemAmt: '매입금액',
	}

	const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<PUR1040_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: PUR1040_R01DetailDto) => renderIndexCell(
				params, 
				(innerParams: PUR1040_R01DetailDto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedPUR1040_R01DetailList,
						selectedList: selectedPUR1040_R01DetailList,
						dataList: PUR1040_R01DetailList
					}),
					() => {},
					() => {},
			)
		),
		{
			headerName: headerNames.itemCd, colId: 'itemCd', field: 'itemCd', width: 120,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.itemNm, colId: 'itemNm', field: 'itemNm', width: 200,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.itemSpec, colId: 'itemSpec', field: 'itemSpec', width: 180,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
			editable: false,
			headerClass: aggridHeaderClass,
		} as ColDef<PUR1040_R01DetailDto>,
		{
			headerName: headerNames.modelNo, colId: 'modelNo', field: 'modelNo', width: 140,
			cellStyle: (p) => getCellStyle(p, 'modelNo'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.brandNm, colId: 'brandNm', field: 'brandNm', width: 100,
			cellStyle: (p) => getCellStyle(p, 'brandNm'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.itemGroupNm, colId: 'itemGroupNm', field: 'itemGroupNm', width: 110,
			cellStyle: (p) => getCellStyle(p, 'itemGroupNm'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.itemAccountNm, colId: 'itemAccountNm', field: 'itemAccountNm', width: 110,
			cellStyle: (p) => getCellStyle(p, 'itemAccountNm'),
			editable: false,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: headerNames.orderUnit, colId: 'orderUnit', field: 'orderUnit', width: 70,
			cellStyle: (p) => getCellStyle(p, 'orderUnit'),
			headerClass: aggridHeaderClass,
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: headerNames.orderQty, colId: 'orderQty', field: 'orderQty', width: 80,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			editable: false,
			headerClass: aggridHeaderClass,
			comparator: (a, b) => Number(a) - Number(b),
			valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
		},
		{
			headerName: headerNames.orderPrice, colId: 'orderPrice', field: 'orderPrice', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderPrice'),
			editable: false,
			headerClass: aggridHeaderClass,
			comparator: (a, b) => Number(a) - Number(b),
			valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
		},
		{
			headerName: headerNames.dcAmt, colId: 'dcAmt', field: 'dcAmt', width: 100,
			cellStyle: (p) => getCellStyle(p, 'dcAmt'),
			editable: false,
			headerClass: aggridHeaderClass,
			comparator: (a, b) => Number(a) - Number(b),
			valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
		},
		{
			headerName: headerNames.itemAmt, colId: 'itemAmt', field: 'itemAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'itemAmt'),
			editable: false,
			headerClass: aggridHeaderClass,
			comparator: (a, b) => Number(a) - Number(b),
			valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
		},
	], [getCellStyle, deletedPUR1040_R01DetailList, selectedPUR1040_R01DetailList, PUR1040_R01DetailList, renderIndexCell, unitList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PUR1040_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PUR1040_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PUR1040_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedPUR1040_R01DetailList,
		deletedList: deletedPUR1040_R01DetailList,
		addSelected: addSelectedPUR1040_R01DetailList,
		removeSelected: removeSelectedPUR1040_R01DetailList,
		clickable: true, setClickable: () => {}
	});


	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = PUR1040_R01DetailList
			.filter((item: PUR1040_R01DetailDto) => !deletedPUR1040_R01DetailList.includes(item.valueIndex.toString()))
			.reduce((sum: number, item: PUR1040_R01DetailDto) => sum + (Number(item.orderQty) || 0), 0);
		const totalAmt : number = PUR1040_R01DetailList
			.filter((item: PUR1040_R01DetailDto) => !deletedPUR1040_R01DetailList.includes(item.valueIndex.toString()))
			.reduce((sum: number, item: PUR1040_R01DetailDto) => sum + (Number(item.itemAmt) || 0), 0);

		return [{
			itemCd: PUR1040_R01DetailList.length.toLocaleString() + "건",
			orderQty: totalQty.toLocaleString(),
			itemAmt: totalAmt.toLocaleString()
		}];	  
	}, [PUR1040_R01DetailList, deletedPUR1040_R01DetailList]);

	return (
		<GridContextWrapper<PUR1040_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setPUR1040_R01DetailList}
			initSelected={initSelectedPUR1040_R01DetailList}
			defaultRowData={defaultPUR1040_R01DetailDto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>

			<Grid_Touch<PUR1040_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}

				rowClassRules={createRowClassRules<PUR1040_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedPUR1040_R01DetailList,
					deletedList: deletedPUR1040_R01DetailList
				})}

				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRowData}
				
				// Grid_Touch 필수 props
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	)
}
